import { Injectable } from '@angular/core';
import mqtt from 'mqtt/dist/mqtt';
import { AuthService } from './auth.service';

export type MqttActionResult = 'sent' | 'offline';

// One-off commands to a machine over the Frigg MQTT brokers, the same way the
// support service popup talks to it: the admin's own Firebase token logs in,
// and the machine listens on systems/<number>/actions. Each call opens its own
// short connection, so pages that only send the odd command (Test channel)
// don't keep a broker connection open.
@Injectable({ providedIn: 'root' })
export class SystemMqttService {

  // The machine keeps a retained systems/<number>/status ('online', or
  // 'offline' from its last will). Waited for this long before sending.
  private readonly statusWaitMs = 3000;
  private readonly timeoutMs = 10000;

  constructor(private auth: AuthService) { }

  // Resolves 'sent' once the broker has the command, 'offline' (nothing sent)
  // when the machine says it is offline. Rejects on login/connection errors.
  async sendAction(systemNumber: string | number, type: string, payload: any): Promise<MqttActionResult> {
    if (systemNumber === undefined || systemNumber === null || systemNumber === '') {
      throw new Error('This system has no system number');
    }
    const token = await this.auth.getToken();
    if (!token || typeof token !== 'string') throw new Error('Not logged in');

    const server = Math.floor(Math.random() * 2) + 1;
    const client = mqtt.connect(`wss://prod${server}.friggkantine.no:9091/?token=${token}`, {
      keepalive: 30,
      reconnectPeriod: 0,
      clientId: 'frigg-admin-action-' + Math.random().toString(16).slice(2, 10)
    });
    const statusTopic = `systems/${systemNumber}/status`;

    return new Promise<MqttActionResult>((resolve, reject) => {
      let done = false;
      const finish = (err: Error | null, result?: MqttActionResult) => {
        if (done) return;
        done = true;
        clearTimeout(overall);
        clearTimeout(statusWait);
        client.end(true);
        err ? reject(err) : resolve(result);
      };
      const overall = setTimeout(() => finish(new Error('No answer from the MQTT broker')), this.timeoutMs);
      let statusWait: any = null;

      const publish = () => {
        client.publish(`systems/${systemNumber}/actions`, JSON.stringify({ type, payload }), { qos: 1 },
          err => err ? finish(err) : finish(null, 'sent'));
      };

      client.on('error', err => finish(err instanceof Error ? err : new Error(String(err))));
      client.on('message', (topic, message) => {
        if (topic !== statusTopic || statusWait === null) return;
        clearTimeout(statusWait);
        statusWait = null;
        let status = '';
        try { status = JSON.parse(message.toString()).status; } catch { }
        if (status === 'offline') finish(null, 'offline');
        else publish();
      });
      client.on('connect', () => {
        // No retained status within the wait: send anyway (older software).
        statusWait = setTimeout(() => { statusWait = null; publish(); }, this.statusWaitMs);
        client.subscribe(statusTopic, err => {
          if (err) { clearTimeout(statusWait); statusWait = null; publish(); }
        });
      });
    });
  }
}
